<!--
	@component
	The styled `Input` component supports automatic field validation using the form context (remember to add a `name` attribute!).
  
  It also supports all standard HTML input attributes (e.g. autocomplete and placeholder)
-->
<script lang="ts">
  import { utils } from '../../utils'
  import { page } from '$app/stores'
  import { hasContext, getContext, createEventDispatcher } from 'svelte'
  import { contextKey } from '../../contexts/form'
  import type { FormContext } from '../../types/form'
  import Button from './Button.svelte'
  import FieldLabel from './FieldLabel.svelte'
  import FieldError from './FieldError.svelte'
  import FieldHint from './FieldHint.svelte'
  import Icon from './Icon.svelte'
  import type {
    HTMLInputAttributes,
    HTMLInputTypeAttribute,
  } from 'svelte/elements'
  import type { InputVariant } from '../../types/input'

  interface $$Props extends HTMLInputAttributes {
    type: HTMLInputTypeAttribute
    value?: string | number
    /** The caption for the associated input. */
    label?: string
    /** Error message for the associated form control. If there are multiple errors, only the first will be shown. */
    error?: string | string[]
    /** Helper text for providing context to the input. The hint will be hidden if there are any errors. */
    hint?: string
    name?: string
    id?: string
    hidden?: boolean
    autofocus?: boolean
    prependIcon?: string
    appendIcon?: string
    group?: string | string[]
    checked?: boolean
    multiple?: boolean
    rounded?: boolean
    variant?: InputVariant
    block?: boolean
    /** Label for button when choosing file for type file */
    fileButtonLabel?: string
  }

  const { formFieldErrors, getFieldConstraints, validateField } = hasContext(
    contextKey,
  )
    ? (getContext(contextKey) as FormContext)
    : {
        formFieldErrors: undefined,
        getFieldConstraints: undefined,
        validateField: undefined,
      }

  export let name: string | undefined = undefined
  export let type: HTMLInputTypeAttribute = 'text'
  export let value: string | number | null = (name && $page.form?.[name]) ?? ''
  export let label: string | undefined = undefined
  export let error: string | string[] | undefined = undefined
  export let hint: string | undefined = undefined
  export let id: string | undefined = undefined
  export let hidden = false
  export let autofocus = false
  export let prependIcon: string | undefined = undefined
  export let appendIcon: string | undefined = undefined
  export let group: string | string[] | undefined = undefined
  export let checked: boolean | undefined = undefined
  export let multiple: boolean | undefined = undefined
  export let rounded = false
  export let variant: InputVariant = 'contained'
  export let block = false
  export let fileButtonLabel: string | undefined = undefined

  let input: HTMLInputElement | undefined
  const fieldId = id || `input-${utils.getRandomString(8)}`

  let checkboxChecked =
    type === 'checkbox' &&
    ((group ? group.includes(value as string) : false) || checked)

  $: if (typeof checked === 'boolean' && input) {
    input.checked = checked
  }

  const dispatch = createEventDispatcher<{
    'group-update': { group: string[] }
  }>()

  const fieldConstraints = getFieldConstraints
    ? getFieldConstraints(name)
    : undefined

  export function focus() {
    input?.focus()
  }

  export function blur() {
    input?.blur()
  }

  export function dispatchInputEvent(value: string) {
    if (input) {
      input.value = value
      input.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  export function files() {
    return input?.files
  }

  export function setFiles(files: FileList) {
    if (input) {
      input.files = files
    }
  }

  function handleInput(event: Event) {
    /** Coerces string values for numeric inputs to numbers, this is usually done out of the box with Svelte but needs to be done manually to allow binding values together with dynamic type attributes. */
    const newValue = (event.target as HTMLInputElement).value

    if (type !== 'file') {
      value =
        (type === 'range' || type === 'number') && newValue !== ''
          ? parseFloat(newValue)
          : newValue
    }

    if (type === 'checkbox') {
      checkboxChecked = input?.checked

      if (group && Array.isArray(group)) {
        input?.checked
          ? group.push(newValue)
          : (group = group.filter((item: string) => item !== newValue))

        dispatch('group-update', { group })
      }
    }

    if (type === 'radio') {
      group = newValue
    }

    if (validateField) {
      validateField(event)
    }
  }

  $: errors = error || (name && $formFieldErrors?.[name])

  function passClickToInput() {
    input?.click()
  }

  let dragging = false
  let originX = 0
  let thumbOriginOffset = 0
  let slider: HTMLButtonElement | undefined
  let thumb: HTMLDivElement | undefined

  function handleRangeClick(event: MouseEvent) {
    const offset = event.offsetX
    updateSliderPosition(offset)
  }

  function startDragging(event: MouseEvent | TouchEvent) {
    const newOriginX =
      'clientX' in event ? event.clientX : (event.touches[0]?.clientX ?? 0)

    if (thumb) {
      dragging = true
      originX = newOriginX
      thumbOriginOffset = thumb.offsetLeft
    }
  }

  function drag(event: MouseEvent | TouchEvent) {
    if (type === 'range') {
      event.preventDefault()

      if (dragging) {
        const clientX =
          'clientX' in event ? event.clientX : (event.touches[0]?.clientX ?? 0)
        const offset = clientX - originX + thumbOriginOffset
        updateSliderPosition(offset)
      }
    }
  }

  function updateSliderPosition(offset: number) {
    if (slider) {
      const width = slider.offsetWidth
      const percentage = offset / width
      const min = parseFloat($$props.min)
      const max = parseFloat($$props.max)
      const step = parseFloat($$props.step)
      const exactValue = percentage * (max - min) + min

      const roundedToClosestStep = Math.max(
        min,
        Math.ceil(exactValue / step) * step,
      )

      const clampedValue = Math.min(max, roundedToClosestStep)
      value = clampedValue
    }
  }
</script>

<svelte:document
  on:mouseup={() => (dragging = false)}
  on:touchcancel={() => (dragging = false)}
  on:touchend={() => (dragging = false)}
  on:mousemove={drag}
  on:touchmove={drag}
/>
<div
  class="input-field {type} {variant}"
  class:label
  class:hidden
  class:block
  class:disabled={Boolean($$restProps.disabled)}
>
  {#if $$slots.label}
    <FieldLabel invalid={!!errors} htmlFor={fieldId}>
      <slot name="label" />
    </FieldLabel>
  {:else if label}
    <FieldLabel invalid={!!errors} htmlFor={fieldId}>
      {label}
    </FieldLabel>
  {/if}
  <div class="input-container {variant}" class:label class:rounded>
    {#if prependIcon}
      <div class="prepend">
        <Icon icon={prependIcon} />
      </div>
    {:else if type === 'search'}
      <div class="prepend">
        <Icon icon="ph-magnifying-glass" />
      </div>
    {/if}
    {#if type === 'checkbox' && checkboxChecked}
      <Icon icon="ph-check-square-fill" size={1.3} />
    {:else if type === 'checkbox'}
      <Icon icon="ph-square" size={1.3} />
    {/if}
    {#if type === 'radio' && group === value}
      <Icon icon="ph-radio-button-fill" size={1.3} />
    {:else if type === 'radio'}
      <Icon icon="ph-circle" size={1.3} />
    {/if}
    <!-- svelte-ignore a11y-autofocus -->
    <input
      {type}
      {name}
      value={value ?? ''}
      id={fieldId}
      hidden={type === 'file' || hidden ? true : undefined}
      autofocus={(!$formFieldErrors && autofocus) || undefined}
      {...fieldConstraints}
      {...$$restProps}
      bind:this={input}
      on:input={handleInput}
      on:blur={validateField}
      on:blur
      on:change
      on:keydown
      on:click
      data-invalid={!!errors || undefined}
      {multiple}
    />
    {#if type === 'file'}
      <Button rounded type="button" on:click={passClickToInput}>
        {fileButtonLabel || `Choose ${multiple ? 'file(s)' : 'file'}`}
      </Button>
    {/if}
    {#if type === 'range'}
      <div class="range-container">
        <div class="track" class:disabled={Boolean($$restProps.disabled)}></div>
        <div class="button-container">
          <button
            type="button"
            role="slider"
            aria-valuenow={typeof value === 'string'
              ? parseFloat(value)
              : (value ?? 0)}
            on:click={handleRangeClick}
            bind:this={slider}
            aria-label="Slider"
          ></button>
          <div
            class="fill"
            style={`width: ${(((typeof value === 'string' ? parseFloat(value) : (value ?? 0)) - $$props.min) / ($$props.max - $$props.min)) * 100}%`}
          ></div>
          <div
            class="thumb"
            role="button"
            tabindex="0"
            style={`left: ${(((typeof value === 'string' ? parseFloat(value) : (value ?? 0)) - $$props.min) / ($$props.max - $$props.min)) * 100}%`}
            bind:this={thumb}
            on:mousedown={startDragging}
            on:touchstart={startDragging}
          ></div>
        </div>
      </div>
    {/if}
    {#if appendIcon}
      <div class="append">
        <Icon icon={appendIcon} />
      </div>
    {:else if type === 'date'}
      <div class="append">
        <Icon icon="ph-calendar-blank" size={1.3} />
      </div>
    {/if}
  </div>
  {#if errors}
    <FieldError error={errors} />
  {/if}

  {#if !errors && hint}
    <FieldHint {hint} />
  {/if}

  {#if type === 'search'}
    <button class="search-submit" type="submit" aria-label="Submit"></button>
  {/if}
</div>

<style lang="scss">
  .input-field {
    text-align: start;
    display: flex;

    input[disabled] {
      background: var(--input-background-disabled);
    }

    input[type='date'] {
      position: relative;
    }

    input[type='date']::-webkit-calendar-picker-indicator {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      height: auto;
      color: transparent;
      background: transparent;
    }

    input[type='date']::-webkit-inner-spin-button {
      z-index: 1;
    }

    input[type='date']::-webkit-clear-button {
      z-index: 1;
    }

    input:-webkit-autofill,
    input:-webkit-autofill:focus {
      transition:
        background-color 600000s 0s,
        color 600000s 0s;
    }

    input[data-autocompleted] {
      background-color: transparent !important;
    }

    &.range.label {
      flex-direction: column;
      gap: 2em;
      margin-bottom: 1em;
    }

    input[type='search'] {
      appearance: textfield;
    }

    &.hidden {
      display: none;
    }

    &.block {
      width: 100%;
    }
  }

  .date,
  .datetime-local,
  .email,
  .file,
  .number,
  .password,
  .search,
  .submit,
  .tel,
  .text,
  .time,
  .url,
  .week {
    flex-direction: column;

    .input-container {
      display: flex;
      margin: var(--input-margin);
      --icon-color: var(--input-icon-color, #000);

      &:not(.label) {
        margin-top: 0;
      }
    }

    &:not(.file) {
      .input-container {
        box-sizing: border-box;
        background: var(--input-background);
        border: var(--input-border);
        border-radius: var(--input-border-radius);
        box-shadow: var(--input-box-shadow);
        transition: background-color 0.125s;
        height: var(--input-height);

        &:focus-within {
          outline-color: -webkit-focus-ring-color;
          outline-style: auto;
          outline-width: 1px;
          overflow: hidden;
        }

        &.rounded {
          border-radius: calc(1em * 2);
        }

        &.outlined {
          background: transparent;
          border: 1px solid rgba(var(--color-on-surface-rgb), 0.38);
          transition: border 250ms cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: unset;

          &:hover {
            border: 1px solid rgba(var(--color-on-surface-rgb), 0.87);
          }

          &:focus-within {
            border: 1px solid rgba(var(--color-on-surface-rgb), 1);
            outline: none;
          }
        }
      }

      input {
        flex: 1;
        border: none;
        background: none;
        padding: var(--input-padding);
        height: 100%;
        width: 100%;
        outline: none;
        text-overflow: ellipsis;
        font-family: 'Helvetica Neue', sans-serif;
        font-size: 1em;

        &:-webkit-autofill {
          border-radius: var(--input-border-radius);
        }
      }

      .prepend,
      .append {
        display: flex;
        align-items: center;
        padding: var(--input-padding);
        pointer-events: none;
      }

      .prepend {
        padding-right: 0;
      }
      .append {
        padding-left: 0;
      }
    }
  }

  .checkbox,
  .radio {
    gap: 0.5em;
    align-items: center;
    --label-font-size: 16px;

    .input-container {
      order: -1;
      position: relative;
      width: 40px;
      height: 40px;
      align-items: center;
      display: flex;
      justify-content: center;
      border-radius: 50%;
    }

    &.outlined {
      padding: 0.5em 0.25em;
      border: 1px solid rgba(var(--color-on-surface-rgb), 0.38);
      border-radius: var(--input-border-radius);
      transition: border 250ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    .input-container::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 100%;
      background-color: var(--color-on-background);
      opacity: 0;
      pointer-events: none;
    }

    input {
      cursor: pointer;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
    }

    input[disabled] {
      cursor: default;
    }

    &:not(.disabled) {
      .input-container:hover::before {
        opacity: 0.08;
      }

      :global(label) {
        cursor: pointer;
      }
    }
  }

  .checkbox {
    --icon-color: var(--checkbox-color, var(--color-primary));

    &.disabled {
      --icon-color: var(--checkbox-color-disabled, var(--color-disabled));
    }
  }

  .radio {
    --icon-color: var(--radio-color, var(--color-primary));

    &.disabled {
      --icon-color: var(--radio-color-disabled, var(--color-disabled));
    }
  }

  .range {
    .input-container {
      width: 100%;

      input {
        display: none;
      }
    }
  }

  .range-container {
    position: relative;

    .track {
      border-radius: 8px;
      height: 4px;
      opacity: 0.38;
      background-color: var(--color-on-surface);
      cursor: pointer;

      &.disabled {
        cursor: default;
      }
    }

    .button-container {
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: 100%;
        background-color: currentColor;
        opacity: 0;
        pointer-events: none;
      }

      &:not(.disabled):hover::before {
        opacity: 0.04;
      }

      button {
        position: absolute;
        appearance: none;
        background: none;
        border: none;
        opacity: 0;
        width: 100%;
        height: 100%;
        padding: 0;
        cursor: pointer;
      }

      .fill {
        position: absolute;
        left: 0;
        width: 0;
        height: 100%;
        background-color: var(--range-color, var(--color-primary));
        border-radius: 8px;
        pointer-events: none;
      }

      .thumb {
        position: absolute;
        display: flex;
        cursor: pointer;
        left: 0;
        transform: translateX(-50%);
        background-color: var(--range-color, var(--color-primary));
        width: 30px;
        height: 30px;
        border-radius: 100%;
        -webkit-tap-highlight-color: transparent;
        box-shadow:
          0 2px 4px -1px rgba(0, 0, 0, var(--box-shadow-opacity-1, 0.2)),
          0 4px 5px 0 rgba(0, 0, 0, var(--box-shadow-opacity-2, 0.14)),
          0 1px 10px 0 rgba(0, 0, 0, var(--box-shadow-opacity-3, 0.12));
      }
    }
  }

  button.search-submit {
    display: none;
  }
</style>
